import React, {useCallback, useEffect, useMemo, useState} from 'react';
import Select from 'react-select';
import moment from 'moment';
import {connect} from 'react-redux';
import {toast} from 'react-toastify';
import {closeModal, plus, times, uploadCsv} from '../../assets/images/images';
import * as Actions from '../../store/actions';
import {capitalizeEveryWord, Constants, handleFileInput} from '../../helpers';
import {
  CountryInput,
  CreateAppForm,
  customStyles,
  DropdownIndicator,
  Modal,
  PrimaryButton,
  TableLayout,
} from '../../components';
import useDragAndDrop from '../../hooks/useDragAndDrop';
import EasyLookupDetails from './EasyLookupDetails/index';
import Nigeria from '../../components/CountryLookups/Nigeria';
import Ghana from '../../components/CountryLookups/Ghana';
import Uganda from '../../components/CountryLookups/Uganda';
import Kenya from '../../components/CountryLookups/Kenya';
import SouthAfrica from '../../components/CountryLookups/SouthAfrica';
import Zimbabwe from '../../components/CountryLookups/Zimbabwe';
import Loader from '../../components/Loader';
import {getDownloadUrl} from '../../helpers/batchTemplateDownloadUrl';
import GlobalSearchResultsModal from './GlobalSearchResultsModal';
import {useNavigate} from 'react-router-dom';
import {useDefaultCountry} from '../../hooks/useDefaultCountry';
import {useAllApps} from '../../hooks/useApps';
import {useQueryClient} from 'react-query';
import Zambia from '../../components/CountryLookups/Zambia';
import {customer360LookupDropdownOptions} from '../Customer360/LayoutConstants';
import {buildQueryParams} from '../../helpers/queryParamsBuilder';
import {X} from 'lucide-react';

const CountryComponentMap = ({lookupInputs, ...props}) => {
  const country = lookupInputs?.country?.value;

  const countryComponentMap = {
    ghana: Ghana,
    uganda: Uganda,
    kenya: Kenya,
    zimbabwe: Zimbabwe,
    zambia: Zambia,
    'south-africa': SouthAfrica,
  };
  const Country = countryComponentMap[country] || Nigeria;

  return <Country lookupInputs={lookupInputs} {...props} />;
};

function EasyLookupModals({
  performLookup,
  performBatchLookup,
  changeLookupPage,
  downloadBatchLookup,
  lookupCategory = 'Individual',
  auth: {userDetails},
  lookup: {lookup, lookupPage, loading, l_loading, singleBatch},
  ...otherProps
}) {
  const {
    open,
    setOpen,
    lookupType,
    batch,
    customer360,
    customer_id,
    customer360Dropdown,
  } = otherProps;
  const navigate = useNavigate();
  const {userDefaultCountry, isLoading: defaultLoading} = useDefaultCountry();

  const {userApps, isLoading} = useAllApps();

  const activeCountryName = sessionStorage.getItem('active-country');
  const activeCountry = useMemo(
    () =>
      Constants.lookups.countries.find(
        country => country?.value === activeCountryName,
      ),
    [activeCountryName],
  );
  const [lookupInputs, setLookupInputs] = useState({
    ...Constants?.lookups?.initialLookUpState,
    app: (!isLoading && userApps && userApps[0]) || {},
    country: activeCountry ? activeCountry : userDefaultCountry,
  });
  const [step, setStep] = useState(lookupPage !== undefined ? lookupPage : 1);
  const [file, setFilData] = useState(null);
  const [enable, setEnable] = useState(true);
  const [newAppForm, setNewAppForm] = useState(false);
  const {dragOver, setDragOver, onDragOver, onDragLeave} = useDragAndDrop();
  const queryClient = useQueryClient();

  localStorage.setItem('lookupInputs', JSON.stringify(lookupInputs));
  useEffect(() => {
    const currentInputs = JSON.parse(localStorage.getItem('lookupInputs'));
    setLookupInputs({
      ...currentInputs,
      country: currentInputs?.country || activeCountry || userDefaultCountry,
      app: (!isLoading && userApps && userApps[0]) || {},
    });
  }, [userDefaultCountry, userApps, isLoading, activeCountry]);

  useEffect(() => {
    setStep(lookupPage !== undefined ? lookupPage : 1);
  }, [setStep, lookupPage, step]);

  const {
    country,
    type,
    lookupValue = '',
    app,
    surname,
    country_code,
    company,
  } = useMemo(() => lookupInputs, [lookupInputs]);

  const handleLookup = useCallback(() => {
    const url = Constants.lookups.checkLookupType(
      country?.value || 'nigeria',
      type?.value,
      lookupValue,
    );

    if (!app.value) {
      return toast.error('Please select an app');
    }
    if (file) {
      const path = Constants.lookups.pathUrlTypeCheck(type.value);
      delete file.type;
      performBatchLookup(
        path,
        {...file, app_id: app.value},
        app.value,
        setOpen,
      );
      return;
    }

    const queryParamString = buildQueryParams(lookupInputs);

    let lookupParams = `${url}${
      queryParamString ? `&${queryParamString}` : ''
    }`;

    if (lookupParams?.includes('country_code=&')) {
      lookupParams = lookupParams?.replace('country_code=&', '');
    }

    performLookup({
      data: lookupParams.replace(/\s/g, ''),
      type: type?.value,
      appId: app?.value.replace(/\s/g, ''),
      ...(type.value === 'nuban' && {account_number: lookupValue}),
      customer360,
      navigate,
      queryClient,
      setOpen,
      customer_id,
    });
    changeLookupPage(1);

    if (!loading && type?.value === 'global_search') {
      return setLookupInputs({...Constants.lookups.initialLookUpState, type});
    }

    !loading &&
      setLookupInputs({
        ...Constants.lookups.initialLookUpState,
        type: {},
        app: {},
        country: userDefaultCountry,
      });
  }, [
    app?.value,
    changeLookupPage,
    country?.value,
    customer360,
    customer_id,
    file,
    loading,
    lookupInputs,
    lookupValue,
    navigate,
    performBatchLookup,
    performLookup,
    queryClient,
    setOpen,
    type,
    userDefaultCountry,
  ]);

  const ngPass =
    lookupInputs?.type?.value === 'passport' &&
    lookupInputs?.country?.value === 'nigeria';

  const onDrop = e => {
    e.preventDefault();
    setDragOver(false);
    const selectedFile = e?.dataTransfer?.files[0];
    if (selectedFile.type.split('/')[0] !== 'application') {
      return toast.error('Please provide a valid file type (.xlsx, .csv)');
    }
    handleFileInput(selectedFile, setFilData);
  };

  useEffect(() => {
    if (file) {
      setEnable(false);
    } else if (ngPass && !!surname && lookupValue) {
      setEnable(false);
    } else if (!ngPass && lookupValue) {
      setEnable(false);
    } else if (country_code && company) {
      setEnable(false);
    } else {
      setEnable(true);
    }
  }, [
    file?.filename,
    lookupValue,
    file,
    ngPass,
    surname,
    country_code,
    company,
  ]);

  const batchUploadProgressPercentage = Math.round(
    (singleBatch?.processed_records / singleBatch?.total_records || 0) * 100,
  );

  const {downloadIndividualLookup: canDownloadHistory} =
    useMemo(() => userDetails?.permissions?.lookupPermissions, [userDetails]) ||
    {};

  const businessIds = useMemo(() => {
    const baseBusinessIds = ['cac', 'cac/basic'];

    if (country?.value === 'nigeria' && !batch) {
      return [...baseBusinessIds, 'cac_advance', 'global_search'];
    }

    return baseBusinessIds;
  }, [country?.value, batch]);

  const getCountryIDs = useCallback(
    key =>
      Constants.lookups.getIDType(
        key || (!defaultLoading && userDefaultCountry?.value) || 'nigeria',
      ),
    [userDefaultCountry, defaultLoading],
  );

  const getFilteredOptions = useMemo(() => {
    const allOptions = getCountryIDs(country?.value);

    if (customer360Dropdown && country?.value === 'nigeria') {
      return customer360LookupDropdownOptions;
    }

    if (lookupCategory === 'Business') {
      let filtered = allOptions?.filter(option =>
        businessIds.includes(option?.value),
      );

      if (
        country?.value === 'nigeria' &&
        !batch &&
        !filtered.some(opt => opt?.value === 'cac_advance')
      ) {
        const cacAdvanceOption = allOptions?.find(
          opt => opt?.value === 'cac_advance',
        );
        if (cacAdvanceOption) {
          filtered = [...filtered, cacAdvanceOption];
        }
      }

      if (lookupType === 'Single' || !batch) {
        filtered = filtered?.filter(option => option?.value !== 'cac/basic');
      }

      if (country?.value === 'nigeria') {
        filtered = filtered.map(option => {
          if (option?.value === 'cac') {
            return {
              ...option,
              label: Constants.lookups.getCACLabel(lookupType, batch),
            };
          }
          return option;
        });
      }

      return filtered;
    }

    if (lookupType === 'Batch' && lookupCategory !== 'Business') {
      return allOptions?.filter(
        option =>
          ![
            ...businessIds,
            'global_search',
            'cac_advance',
            'passport',
          ].includes(option?.value),
      );
    }

    return allOptions?.filter(option => !businessIds.includes(option?.value));
  }, [
    country?.value,
    lookupCategory,
    lookupType,
    businessIds,
    customer360Dropdown,
    getCountryIDs,
    batch,
  ]);

  const SearchResults = lookup?.global_search?.entity;

  const getModalClasses = () => {
    const baseClasses = 'overflow-y-auto absolute bottom-0 sm:relative';

    switch (step) {
      case 1:
        return `max-w-[528px] h-fit max-h-[95%] sm:max-h-full rounded-b-none sm:rounded-b-lg ${baseClasses} lookup-modal`;
      case 2:
        if (lookupInputs?.type?.value === 'global_search') {
          return `max-w-[528px] sm:max-h-full rounded-b-none sm:rounded-b-lg h-fit max-h-[95%] lookup-modal ${baseClasses}`;
        } else {
          return `max-w-[516px] rounded-t-none sm:rounded-lg h-fit max-h-full lookup-modal ${baseClasses}`;
        }
      case 3:
        return `max-w-[516px] rounded-b-none sm:rounded-b-lg h-fit max-h-full ${baseClasses}`;
      default:
        return baseClasses;
    }
  };

  const renderModalContent = () => {
    switch (step) {
      case 1:
        return (
          <div>
            <header className="flex items-center bg-[#F9F9F9] justify-between py-3 px-6 rounded-t-xl border-[0.5px] border-transparent border-b-[#EFEFEF]">
              <h3 className="font-semibold text-deepBlue text-[18px]">
                {lookupType || 'Easy'} Lookup
              </h3>
              <button
                onClick={() => setOpen(false)}
                className="bg-[#EDEDED] rounded-md p-1.5"
              >
                <X size={16} color="#242424" />
              </button>
            </header>

            {l_loading ? (
              <Loader />
            ) : (
              <form className="text-left" style={{opacity: l_loading && 0.4}}>
                <div className="py-4 px-6">
                  {lookupCategory === 'Business' ? null : (
                    <CountryInput
                      value={
                        (activeCountry && activeCountry) ||
                        userDefaultCountry ||
                        lookupInputs?.country ||
                        Constants?.lookups?.countries[0]
                      }
                      onChange={country => {
                        setLookupInputs({
                          ...lookupInputs,
                          ...Constants?.lookups?.initialLookUpState,
                          country: country || userDefaultCountry,
                        });
                        const activeName =
                          country?.value || userDefaultCountry?.value;
                        sessionStorage.setItem('active-country', activeName);
                      }}
                      label="Select country"
                      lookupType={lookupType}
                    />
                  )}
                  <label htmlFor="app" className="relative mb-1 mt-6">
                    Select app
                  </label>
                  <Select
                    defaultValue={
                      lookupInputs?.app || (!isLoading && userApps[0])
                    }
                    value={lookupInputs?.app || (!isLoading && userApps[0])}
                    onChange={app => setLookupInputs({...lookupInputs, app})}
                    options={userApps}
                    placeholder="Select app"
                    components={{DropdownIndicator}}
                    classNamePrefix="react-select"
                    styles={customStyles}
                    isDisabled={isLoading}
                  />
                  <button
                    className="flex items-center gap-[2px] ml-auto mt-2"
                    onClick={() => setNewAppForm(prev => !prev)}
                    type="button"
                  >
                    <img
                      src={newAppForm ? times : plus}
                      alt=""
                      width={12}
                      height={12}
                    />
                    <span className="text-xs font-medium text-body">
                      {newAppForm ? 'Close' : 'Create a new app'}
                    </span>
                  </button>

                  {newAppForm && (
                    <div className="p-6 mt-2 rounded bg-white80 animate-dropdown">
                      <h3 className="text-base font-medium text-grey">
                        Create App
                      </h3>
                      <CreateAppForm
                        setOpen={() => setNewAppForm(prev => !prev)}
                      />
                    </div>
                  )}
                  <label htmlFor="industry" className="relative mt-6 mb-1">
                    Select ID Type
                  </label>

                  {/* THis select ID */}
                  <Select
                    defaultValue={lookupInputs?.type}
                    value={lookupInputs?.type}
                    onChange={type => {
                      setLookupInputs({
                        ...lookupInputs,
                        type,
                      });
                    }}
                    options={getFilteredOptions}
                    placeholder="Select an ID type"
                    components={{DropdownIndicator}}
                    classNamePrefix="react-select"
                    styles={customStyles}
                    menuPlacement="auto"
                    isSearchable={false}
                    key={`${lookupCategory}-${lookupType}-${batch}-${country?.value}`}
                  />

                  {/* Only show CountryComponentMap when NOT in batch mode */}
                  {!batch && (
                    <CountryComponentMap
                      lookupInputs={lookupInputs}
                      setLookupInputs={setLookupInputs}
                      batch={batch}
                    />
                  )}

                  {batch && (
                    <div
                      className={`${
                        lookupInputs.type === null ? 'hidden' : 'block'
                      } mt-8 sm:bg-white80 sm:p-6 rounded`}
                    >
                      <div
                        onDragOver={onDragOver}
                        onDragLeave={onDragLeave}
                        onDrop={onDrop}
                        style={{
                          border: `${dragOver ? '3px dashed #33333322' : ''}`,
                        }}
                        className="mt-6 py-[22px] flex flex-col items-center gap-[2px] text-center border border-brandBlue border-dashed rounded"
                      >
                        <div className="flex-col items-center hidden sm:flex">
                          {!file && (
                            <img
                              src={uploadCsv}
                              alt=""
                              width={48}
                              height={48}
                            />
                          )}
                          <p className="text-body">
                            {dragOver ? (
                              'Please drop file here'
                            ) : (
                              <span style={{color: file ? '#00000032' : ''}}>
                                Drag and drop Excel file here
                              </span>
                            )}
                          </p>
                        </div>
                        {file ? (
                          <div className="text-body text-sm flex my-2 gap-[3px] items-baseline">
                            <input
                              type="file"
                              id="file"
                              onChange={e => {
                                handleFileInput(e.target.files[0], setFilData);
                              }}
                              accept=".xlsx, .csv"
                            />
                            <label htmlFor="file">Change file</label>
                          </div>
                        ) : (
                          <div className="text-body text-sm flex gap-[3px] items-baseline">
                            <span className="hidden sm:block">or</span>
                            <input
                              type="file"
                              id="file"
                              onChange={e => {
                                handleFileInput(e.target.files[0], setFilData);
                              }}
                              accept=".xlsx, .csv"
                            />
                            <label htmlFor="file">click here to select</label>
                            from your device
                          </div>
                        )}
                        <p className="text-body text-sm flex gap-[3px] items-baseline">
                          {file ? (
                            <>
                              File:{' '}
                              <span className="font-bold text-black ">
                                {file.filename}
                              </span>
                            </>
                          ) : (
                            ''
                          )}
                        </p>
                      </div>
                      {[
                        'bvn',
                        'dl',
                        'vnin',
                        'phone_number',
                        'cac',
                        'account_number',
                        'basic',
                        'nin',
                        'advance',
                      ].includes(type?.value) && (
                        <div className="mt-2 text-xs text-right">
                          <a
                            href={getDownloadUrl(type?.value)}
                            target="_blank"
                            rel="noreferrer"
                            download
                            className="font-medium text-body sm:text-brandBlue"
                          >
                            Click here{' '}
                            <span className="font-medium text-body">
                              to download template for batch upload
                            </span>
                          </a>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <div className="w-full px-6 py-[14px] border-[0.5px] border-transparent border-t-[#EFEFEF]">
                  <PrimaryButton
                    disabled={enable}
                    buttonText="Lookup"
                    xPadding="p-[35px]"
                    onClick={handleLookup}
                    className="w-full !h-10"
                    loading={loading}
                  />
                </div>
              </form>
            )}
          </div>
        );

      case 2:
        return lookupInputs?.type?.value === 'global_search' ? (
          <GlobalSearchResultsModal open={open} setOpen={setOpen} />
        ) : (
          <EasyLookupDetails open={open} setOpen={setOpen} />
        );

      case 3:
        return (
          <div className="px-4 pt-10 pb-4 sm:p-10">
            <header className="flex items-center justify-between">
              <div className="flex flex-wrap items-center gap-2">
                <h3 className="text-base font-medium text-grey">
                  {capitalizeEveryWord(
                    singleBatch?.lookup_type.replace('_', ' '),
                  )}{' '}
                  Verification
                </h3>
                <div className="px-2 py-1 text-xs font-medium uppercase rounded-full text-brandBlue50 bg-brandBlue40 w-fit">
                  Batch upload
                </div>
                <div
                  className={`px-2 py-1 text-xs font-medium uppercase rounded-full  w-fit ${
                    batchUploadProgressPercentage === 100
                      ? 'text-success20 bg-success'
                      : 'text-white bg-status-404'
                  }`}
                >
                  {batchUploadProgressPercentage === 100
                    ? 'Successful'
                    : 'Processing'}
                </div>
              </div>
              <button
                onClick={() => {
                  changeLookupPage(1);
                  setOpen(false);
                }}
                className="hidden sm:block"
              >
                <img src={closeModal} alt="" />
              </button>
            </header>

            <div
              className={`relative h-4 mt-4 mb-8 text-xs font-medium ${
                batchUploadProgressPercentage > 50 && 'text-white'
              }  rounded-full bg-brandBlue40`}
            >
              <span className="absolute left-0 right-0 top-1">
                {(!l_loading && batchUploadProgressPercentage) || 0}%
              </span>
              <progress
                id="file"
                value={(!l_loading && batchUploadProgressPercentage) || 0}
                max="100"
              ></progress>
            </div>

            {l_loading ? (
              <Loader height={30} />
            ) : (
              <TableLayout>
                <thead className="text-xs font-semibold uppercase">
                  <tr className="bg-white80">
                    <th className="p-5 text-xs font-medium text-left text-grey whitespace-nowrap">
                      number of id
                    </th>
                    <th className="p-5 text-xs font-medium text-left text-grey whitespace-nowrap">
                      ID type
                    </th>
                    <th className="p-5 text-xs font-medium text-left text-grey whitespace-nowrap">
                      Last checked
                    </th>
                  </tr>
                </thead>
                <tbody className="text-sm">
                  <tr className="text-left border-b cursor-pointer border-grey60">
                    <td className="p-5 whitespace-nowrap">
                      {singleBatch?.total_records}
                    </td>
                    <td className="p-5 whitespace-nowrap">
                      {singleBatch?.lookup_type}
                    </td>
                    <td className="p-5 whitespace-nowrap">
                      {moment(singleBatch?.last_checked).format(
                        'DD MMM YYYY HH:mm',
                      )}
                    </td>
                  </tr>
                </tbody>
              </TableLayout>
            )}
            {!l_loading && (
              <div className=" print:flex print:items-center print:justify-between text-xs text-body uppercase mt-8 mb-3 px-[5.88%]">
                <span>
                  Performed by:{' '}
                  {singleBatch?.caller_name || singleBatch?.caller_email
                    ? singleBatch?.caller_name || singleBatch?.caller_email
                    : '-'}
                </span>
                <span className="ml-8">
                  Generated:{' '}
                  {singleBatch?.date_created
                    ? moment(singleBatch?.date_created).format(
                        'MMM DD, YYYY hh:mmA',
                      )
                    : moment(new Date()).format('MMM DD, YYYY hh:mmA') || '-'}
                </span>
              </div>
            )}
            <div className="flex flex-col items-center gap-4 mt-6 sm:flex-row">
              {/* <Select
              defaultValue={lookupInputs?.bank_code}
              value={lookupInputs?.bank_code}
              onChange={bank_code =>
                setLookupInputs({...lookupInputs, bank_code})
              }
              options={[
                {
                  value: 'xlsx',
                  label: 'XLSX',
                  name: 'XLSX',
                },
                {
                  value: 'pdf',
                  label: 'PDF',
                  name: 'PDF',
                },
              ]}
              placeholder="Select Type"
              components={{DropdownIndicator}}
              classNamePrefix="react-select"
              styles={customStyles}
              isSearchable={false}
              menuPlacement="top"
            /> */}
              <button className="flex items-center justify-between sm:justify-start gap-4 py-[14px] px-4 text-tiny text-grey80 bg-white80 outline outline-1 outline-grey60 rounded w-full sm:w-fit focus-visible:outline-brandBlue">
                XLSX
                {/* <img src={dropdown} alt="" width={16} height={16} /> */}
              </button>
              <PrimaryButton
                buttonText="Download"
                className="flex-1 w-full py-4 rounded sm:w-fit"
                onClick={() => {
                  downloadBatchLookup(singleBatch?.id, () => {
                    setOpen(false);
                    changeLookupPage(1);
                  });
                }}
                loading={loading}
                disabled={!canDownloadHistory}
                style={{cursor: !canDownloadHistory && 'not-allowed'}}
                title={
                  canDownloadHistory
                    ? 'Download history'
                    : 'You do not have permission to download lookup history'
                }
              />
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <Modal
      show={open}
      onClose={setOpen}
      extraModalClasses={getModalClasses()}
      modalPosition="justify-center sm:justify-end"
      modalTitle={
        step === 2 && lookupInputs?.type?.value === 'global_search' ? (
          <div className="flex items-center gap-1">
            <h3 className="text-base font-medium text-grey">Global Check</h3>
            <span className="w-5 h-5 bg-brandBlue flex items-center justify-center rounded-full text-white text-xs font-medium">
              {SearchResults?.length || 0}
            </span>
          </div>
        ) : undefined
      }
    >
      {renderModalContent()}
    </Modal>
  );
}

export default connect(state => state, Actions)(EasyLookupModals);
